// eslint-disable-next-line jira/restricted/@atlassian/react-sweet-state
import {
	createActionsHook,
	createStateHook,
	createStore,
	type Action,
} from '@atlassian/react-sweet-state';

import { autoBeforeEachTest } from '@atlassian/jira-platform-auto-before-each-test/src/auto-before-each-test.tsx';
import { callWhenIdle } from '@atlassian/jira-call-when-idle';
import { fg } from '@atlassian/jira-feature-gating';

type State = { renderMode: 'hydrateRoot'; isComplete: boolean } | { renderMode: 'createRoot' };
const DEFAULT_STATE: State = { renderMode: 'createRoot' };
let state: State = Object.freeze(DEFAULT_STATE);

// Even though this uses no hooks, we expose it as one to ensure it is only used during render
// (which is the only time we know if we're hydrating)
// For that reason, though, it's important that this never changes value
export const useIsHydrateRenderMode = (): boolean => state.renderMode === 'hydrateRoot';

export const useSsrPlaceholders = (): boolean =>
	state.renderMode !== 'hydrateRoot' || !fg('jfp-magma-data-ssr-placeholder-retain');

/**
 * Indicates that we are rendering React using `hydrateRoot`. This should be called once only early in init before any
 * rendering, since it's assumed to remain constant in {@link useIsHydrateRenderMode}
 */
export const setIsHydrationRenderMode = (): void => {
	state = { renderMode: 'hydrateRoot', isComplete: false };
};

/**
 * Indicates that hydration is finished. Ignored if not in hydrate root mode.
 */
export const reportHydrationEnd = (): void => {
	if (state.renderMode === 'hydrateRoot') {
		state.isComplete = true;
	}
};

export const getIsHydrationInProgress = (): boolean =>
	state.renderMode === 'hydrateRoot' && !state.isComplete;

autoBeforeEachTest(() => {
	state = DEFAULT_STATE;
});

let clearCompletionSubscription: (() => void) | null = null;

type StoreState = { isComplete: boolean; pendingIds: string[] };
const actions = {
	reportHydrationEnd:
		(): Action<StoreState> =>
		({ setState }) => {
			reportHydrationEnd();
			setState({ isComplete: true });
		},
	addPendingSubtree:
		(componentId: string): Action<StoreState> =>
		({ getState, setState }) => {
			if (!getIsHydrationInProgress()) {
				return;
			}

			const { pendingIds } = getState();
			if (!pendingIds.includes(componentId)) {
				setState({ pendingIds: [...pendingIds, componentId] });
			}
		},
	completePendingSubtree:
		(componentId: string): Action<StoreState> =>
		({ getState, setState }) => {
			if (!getIsHydrationInProgress()) {
				return;
			}

			const previousPendingIds = getState().pendingIds;
			const updatedPendingIds = previousPendingIds.filter((pendingId) => pendingId !== componentId);
			setState({ pendingIds: updatedPendingIds });

			if (previousPendingIds.includes(componentId) && updatedPendingIds.length === 0) {
				if (clearCompletionSubscription != null) {
					clearCompletionSubscription();
				}
				// Add a delay before checking for completion because the pending set becomes empty several times as React
				// hydrates each "layer".
				clearCompletionSubscription = callWhenIdle(() => {
					clearCompletionSubscription = null;
					if (getState().pendingIds.length === 0) {
						reportHydrationEnd();
						setState({ isComplete: true });
					}
				}, /* timeout for browsers without requestIdleCallback */ 2_000);
			}
		},
};
type Actions = typeof actions;

/**
 * We need access to global hydration state inside React (reactive) and outside React, so have both a store and module state.
 */
const store = createStore<StoreState, Actions>({
	name: 'hydration-global-completion',
	initialState: () => ({ isComplete: false, pendingIds: [] }),
	actions,
});

/**
 * Indicates if hydration has completed globally - all subtrees have completed hydrating.
 * NB: this only makes sense to request if {@link useIsHydrateRenderMode} is true.
 */
export const useIsGlobalHydrationComplete: () => boolean = createStateHook(store, {
	selector: ({ isComplete }) => isComplete,
});

export const usePendingIdsForTesting: () => string[] = createStateHook(store, {
	selector: ({ pendingIds }) => pendingIds,
});

export const useGlobalHydrationActions: () => {
	reportHydrationEnd: () => void;
	addPendingSubtree: (componentId: string) => void;
	completePendingSubtree: (componentId: string) => void;
} = createActionsHook(store);
